import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Calendar,
  Clock,
  AlertTriangle,
  History,
  ArrowRight,
  TrendingUp,
  Info,
  CalendarClock,
  Timer,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { Task, DueDateChange } from '@/types';

interface DueDateUpdateModalProps {
  open: boolean;
  task: Task;
  onClose: () => void;
  onUpdate: (newDueDate: string, reason: string) => Promise<void>;
}

interface DueDateHistoryProps {
  task: Task;
  showUpdateButton?: boolean;
  onUpdateClick?: () => void;
}

// Calculate delay days between two dates
const calculateDelayDays = (originalDate: string, newDate: string): number => {
  const original = dayjs(originalDate);
  const updated = dayjs(newDate);
  return updated.diff(original, 'day');
};

// Get delay severity
const getDelaySeverity = (delayDays: number) => {
  if (delayDays <= 0) return { color: '#16a34a', bgColor: '#dcfce7', label: 'On Time' };
  if (delayDays <= 3) return { color: '#ca8a04', bgColor: '#fef9c3', label: 'Minor Delay' };
  if (delayDays <= 7) return { color: '#ea580c', bgColor: '#ffedd5', label: 'Moderate Delay' };
  if (delayDays <= 14) return { color: '#dc2626', bgColor: '#fee2e2', label: 'Significant Delay' };
  return { color: '#991b1b', bgColor: '#fecaca', label: 'Critical Delay' };
};

/**
 * DueDateUpdateModal
 * Modal for updating task due date with reason tracking
 */
const DueDateUpdateModal: React.FC<DueDateUpdateModalProps> = ({
  open,
  task,
  onClose,
  onUpdate,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newDueDate, setNewDueDate] = useState<Dayjs | null>(dayjs(task.dueDate));
  const [reason, setReason] = useState('');

  const originalDueDate = task.originalDueDate || task.dueDate;
  const currentDueDate = task.dueDate;

  // Calculate delays
  const delayFromOriginal = newDueDate 
    ? calculateDelayDays(originalDueDate, newDueDate.format('YYYY-MM-DD'))
    : 0;
  const delayFromCurrent = newDueDate 
    ? calculateDelayDays(currentDueDate, newDueDate.format('YYYY-MM-DD'))
    : 0;

  const delaySeverity = getDelaySeverity(delayFromOriginal);

  const handleSubmit = async () => {
    if (!newDueDate || !reason.trim()) return;
    
    setIsSubmitting(true);
    try {
      // Use date-only format to avoid timezone shifts
      const dateOnly = newDueDate.format('YYYY-MM-DD');
      await onUpdate(dateOnly, reason.trim());
      setReason('');
      onClose();
    } catch (error) {
      console.error('Failed to update due date:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickReasons = [
    'Scope change',
    'Resource constraints',
    'Dependency delay',
    'Technical complexity',
    'Client request',
    'Priority change',
    'Testing requirements',
    'External blockers',
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarClock size={24} style={{ color: '#f59e0b' }} />
          Update Due Date
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Current Status */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Timeline
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Original Due Date
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {dayjs(originalDueDate).format('MMM D, YYYY')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Current Due Date
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {dayjs(currentDueDate).format('MMM D, YYYY')}
                </Typography>
              </Box>
            </Box>
            {task.totalDelayDays && task.totalDelayDays > 0 && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={<Timer size={14} />}
                  label={`Total delay: ${task.totalDelayDays} days`}
                  size="small"
                  sx={{
                    bgcolor: getDelaySeverity(task.totalDelayDays).bgColor,
                    color: getDelaySeverity(task.totalDelayDays).color,
                  }}
                />
              </Box>
            )}
          </Paper>

          {/* New Due Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="New Due Date"
              value={newDueDate}
              onChange={(date) => setNewDueDate(date)}
              minDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          {/* Delay Preview */}
          {newDueDate && delayFromCurrent !== 0 && (
            <Alert 
              severity={delayFromCurrent > 0 ? 'warning' : 'success'}
              icon={delayFromCurrent > 0 ? <AlertTriangle size={20} /> : <TrendingUp size={20} />}
            >
              <Typography variant="body2">
                {delayFromCurrent > 0 ? (
                  <>
                    This will add <strong>{delayFromCurrent} day{delayFromCurrent !== 1 ? 's' : ''}</strong> to the current due date.
                    {delayFromOriginal > 0 && (
                      <span> Total delay from original: <strong>{delayFromOriginal} day{delayFromOriginal !== 1 ? 's' : ''}</strong></span>
                    )}
                  </>
                ) : (
                  <>
                    This will reduce the deadline by <strong>{Math.abs(delayFromCurrent)} day{Math.abs(delayFromCurrent) !== 1 ? 's' : ''}</strong>.
                  </>
                )}
              </Typography>
            </Alert>
          )}

          {/* Delay Severity Badge */}
          {newDueDate && delayFromOriginal > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Chip
                icon={<AlertTriangle size={14} />}
                label={delaySeverity.label}
                sx={{
                  bgcolor: delaySeverity.bgColor,
                  color: delaySeverity.color,
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: delaySeverity.color },
                }}
              />
            </Box>
          )}

          {/* Reason */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Reason for change *
            </Typography>
            <TextField
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why the due date needs to be changed..."
              fullWidth
              required
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {quickReasons.map((quickReason) => (
                <Chip
                  key={quickReason}
                  label={quickReason}
                  size="small"
                  variant="outlined"
                  onClick={() => setReason(reason ? `${reason}. ${quickReason}` : quickReason)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Previous Changes */}
          {task.dueDateHistory && task.dueDateHistory.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History size={16} />
                Previous Changes ({task.dueDateHistory.length})
              </Typography>
              <List dense disablePadding sx={{ maxHeight: 150, overflow: 'auto' }}>
                {task.dueDateHistory.slice(-3).reverse().map((change, index) => (
                  <ListItem key={change.id} sx={{ px: 1, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                          <Typography variant="caption">
                            {dayjs(change.previousDueDate).format('MMM D')}
                          </Typography>
                          <ArrowRight size={12} />
                          <Typography variant="caption">
                            {dayjs(change.newDueDate).format('MMM D')}
                          </Typography>
                          <Chip
                            label={`+${change.delayDays}d`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: getDelaySeverity(change.delayDays).bgColor,
                              color: getDelaySeverity(change.delayDays).color,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {change.reason}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={!newDueDate || !reason.trim() || delayFromCurrent === 0 || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Calendar size={16} />}
        >
          Update Due Date
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * DueDateHistory
 * Component to display due date change history inline
 */
export const DueDateHistory: React.FC<DueDateHistoryProps> = ({
  task,
  showUpdateButton = true,
  onUpdateClick,
}) => {
  const originalDueDate = task.originalDueDate || task.dueDate;
  const currentDueDate = task.dueDate;
  const history = task.dueDateHistory || [];
  const totalDelay = task.totalDelayDays || 0;

  const isOverdue = dayjs(currentDueDate).isBefore(dayjs(), 'day') && task.status !== 'completed';
  const overdueBy = isOverdue ? dayjs().diff(dayjs(currentDueDate), 'day') : 0;

  const delaySeverity = getDelaySeverity(totalDelay + overdueBy);

  return (
    <Box>
      {/* Current Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Calendar size={16} style={{ color: '#64748b' }} />
          <Typography variant="body2" sx={{ color: isOverdue ? 'error.main' : 'text.primary' }}>
            {dayjs(currentDueDate).format('MMM D, YYYY')}
          </Typography>
        </Box>

        {isOverdue && (
          <Chip
            icon={<AlertTriangle size={14} />}
            label={`Overdue by ${overdueBy} day${overdueBy !== 1 ? 's' : ''}`}
            size="small"
            color="error"
          />
        )}

        {totalDelay > 0 && (
          <Tooltip title={`Originally due: ${dayjs(originalDueDate).format('MMM D, YYYY')}`}>
            <Chip
              icon={<Timer size={14} />}
              label={`${totalDelay}d delayed`}
              size="small"
              sx={{
                bgcolor: delaySeverity.bgColor,
                color: delaySeverity.color,
                '& .MuiChip-icon': { color: delaySeverity.color },
              }}
            />
          </Tooltip>
        )}

        {history.length > 0 && (
          <Tooltip title={`${history.length} previous date change${history.length !== 1 ? 's' : ''}`}>
            <Chip
              icon={<History size={14} />}
              label={history.length}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        )}

        {showUpdateButton && (isOverdue || task.status !== 'completed') && (
          <Tooltip title="Update due date">
            <IconButton size="small" onClick={onUpdateClick}>
              <CalendarClock size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* History Preview */}
      {history.length > 0 && (
        <Box sx={{ mt: 1, pl: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Last change: {history[history.length - 1].reason}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * DueDateSummary
 * Compact summary of due date status with delay tracking
 */
export const DueDateSummary: React.FC<{ task: Task; onUpdateClick?: () => void }> = ({
  task,
  onUpdateClick,
}) => {
  const originalDueDate = task.originalDueDate || task.dueDate;
  const totalDelay = task.totalDelayDays || 0;
  const isOverdue = dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== 'completed';
  const overdueBy = isOverdue ? dayjs().diff(dayjs(task.dueDate), 'day') : 0;

  if (!isOverdue && totalDelay === 0) {
    return null;
  }

  const delaySeverity = getDelaySeverity(totalDelay + overdueBy);

  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: delaySeverity.bgColor,
        border: '1px solid',
        borderColor: delaySeverity.color,
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AlertTriangle size={18} style={{ color: delaySeverity.color }} />
            <Typography variant="subtitle2" sx={{ color: delaySeverity.color }}>
              {delaySeverity.label}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isOverdue && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Overdue by
                </Typography>
                <Typography variant="body2" fontWeight={500} color="error.main">
                  {overdueBy} day{overdueBy !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}

            {totalDelay > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total delay from original
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {totalDelay} day{totalDelay !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Original due date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {dayjs(originalDueDate).format('MMM D, YYYY')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {onUpdateClick && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarClock size={16} />}
            onClick={onUpdateClick}
            sx={{
              borderColor: delaySeverity.color,
              color: delaySeverity.color,
              '&:hover': {
                borderColor: delaySeverity.color,
                bgcolor: `${delaySeverity.color}10`,
              },
            }}
          >
            Update Date
          </Button>
        )}
      </Box>

      {task.dueDateHistory && task.dueDateHistory.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">
            Date changed {task.dueDateHistory.length} time{task.dueDateHistory.length !== 1 ? 's' : ''}
            {' • '}Last: {task.dueDateHistory[task.dueDateHistory.length - 1].reason}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default DueDateUpdateModal;
